import React, { useState, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useTickets } from '../contexts/TicketContext';
import { ticketsAPI } from '../api/api';
import KanbanView from '../components/board/KanbanView';
import ListView from '../components/board/ListView';
import TicketDetailModal from '../components/modals/TicketDetailModal';
import { CalendarView } from '../components/views/calendar';
import {
  Squares2X2Icon,
  ListBulletIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import useIsMobile from '../hooks/useIsMobile';

const BoardPage = () => {
  const { selectedProject } = useProject();
  const { updateTicketStatus, tickets, setTickets, loadTickets, projectStatuses } = useTickets();
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban', 'list', or 'calendar'
  const isMobile = useIsMobile();

  // Proje icon'unu al
  const getProjectIcon = () => {
    if (selectedProject?.project_icon && selectedProject.project_icon.startsWith('icons8-')) {
      return (
        <img
          src={`/project-icons/${selectedProject.project_icon}.png`}
          alt={selectedProject.project_icon}
          className="w-8 h-8 object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
      );
    }
    return '📁';
  };

  // Proje değiştiğinde ticket'ları yükle - Context'ten otomatik yükleniyor

  // Custom event listener - MainLayout'tan ticket oluşturulduğunda dinle
  useEffect(() => {
    const handleTicketCreated = (event) => {
      // Ticket'ları yeniden yükle - ZORLA
      if (selectedProject?.id) {
        // Context otomatik olarak yeniden yükleyecek
      }
    };

    window.addEventListener('ticketCreated', handleTicketCreated);

    return () => {
      window.removeEventListener('ticketCreated', handleTicketCreated);
    };
  }, [selectedProject?.id]);

  // loadProjectTickets fonksiyonu kaldırıldı - Context'ten otomatik yükleniyor

  // Drag & Drop sonrası ticket status'unu güncelle
  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTicket(null);
  };

  const handleDragEnd = async (ticketId, newStatus) => {
    try {
      await updateTicketStatus(ticketId, newStatus);
      setError(null);
    } catch (error) {
      setError('Ticket status güncellenirken hata oluştu');
    }
  };

  // Takvim sürükle-bırak işlemi
  const handleCalendarDragEnd = async (ticketId, targetDate) => {
    try {
      // Timezone sorununu çözmek için tarihi doğru formatta al
      const year = targetDate.getFullYear();
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      const response = await ticketsAPI.updateTicket(ticketId, {
        due_date: formattedDate
      });

      if (!response.success) {
        setError('Ticket tarihi güncellenirken hata oluştu');
      } else {
        setError(null);
      }
    } catch (error) {
      // Context otomatik olarak yeniden yükleyecek
      setError('Ticket tarihi güncellenirken hata oluştu');
    }
  };

  if (!selectedProject) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-text-secondary">Lütfen bir proje seçin.</p>
      </div>
    );
  }

  // Loading state kaldırıldı - Context'ten otomatik

  // Background image mapping
  const getBackgroundFilename = (backgroundId) => {
    const backgrounds = {
      'default-bg': 'pexels-pixabay-268533.jpg',
      'abstract-blue': 'pexels-pixabay-33545.jpg',
      'abstract-green': 'pexels-eberhardgross-1612353.jpg',
      'gradient-sunset': 'pexels-daniel-maforte-3709229-5544157.jpg',
      'gradient-forest': 'pexels-rpnickson-2775196.jpg',
      'tech-circuit': 'pexels-pok-rie-33563-6029883.jpg',
      'minimal-white': 'pexels-oscar-garcia-medina-241846767-12302975.jpg',
      'nature-beach': 'pexels-nicole-avagliano-1132392-2236703.jpg',
      'hotel-modern': 'pexels-luis-rodrigues-198790-1687575.jpg',
      'geometric-blue': 'pexels-mian-rizwan-250562-1681236.jpg',
      'Sword': 'sword.png',
      'ayrikvadi': 'ayrikvadi.jpg'
    };
    return backgrounds[backgroundId] || backgrounds['default-bg'];
  };

  // Debug: selectedProject değişikliklerini takip et

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage: selectedProject?.background_image
          ? `url(/project-backgrounds/${getBackgroundFilename(selectedProject.background_image)})`
          : 'linear-gradient(135deg, theme("colors.background.main") 0%, theme("colors.background.secondary") 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="relative z-10 p-3 sm:p-4 md:p-6">
        {/* Proje Header */}
        <div className="mb-6 p-4 bg-white/80 dark:bg-dark-background-main/80 backdrop-blur-sm rounded-xl border border-white/20 dark:border-dark-border/30">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10">
                {getProjectIcon()}
                <span className="text-lg hidden">📁</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-text-primary dark:text-dark-text-primary">
                  {selectedProject.project_name}
                </h1>
                <p className="text-sm text-text-secondary dark:text-dark-text-secondary">
                  Proje Kodu: {selectedProject.project_code}
                </p>
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center space-x-2 overflow-x-auto">
              <div className="flex items-center bg-background-secondary dark:bg-dark-background-secondary rounded-lg p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'list'
                    ? 'bg-white dark:bg-dark-surface text-text-primary dark:text-dark-text-primary shadow-sm'
                    : 'text-text-secondary dark:text-dark-text-secondary hover:text-text-primary dark:hover:text-dark-text-primary'
                    }`}
                  title="Liste Görünümü"
                >
                  <ListBulletIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'kanban'
                    ? 'bg-white dark:bg-dark-surface text-text-primary dark:text-dark-text-primary shadow-sm'
                    : 'text-text-secondary dark:text-dark-text-secondary hover:text-text-primary dark:hover:text-dark-text-primary'
                    }`}
                  title="Pano Görünümü"
                >
                  <Squares2X2Icon className="w-4 h-4" />
                </button>
              </div>

              {/* Calendar Button */}
              {!isMobile && <button
                onClick={() => setViewMode(viewMode === 'calendar' ? 'kanban' : 'calendar')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'calendar'
                  ? 'bg-highlight-600 text-white shadow-sm'
                  : 'bg-background-secondary dark:bg-dark-background-secondary text-text-secondary dark:text-dark-text-secondary hover:text-text-primary dark:hover:text-dark-text-primary hover:bg-background-secondary/80 dark:hover:bg-dark-background-secondary/80'
                  }`}
                title="Takvim Görünümü"
              >
                <CalendarDaysIcon className="w-4 h-4" />
              </button>}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-error-50 border border-error-100 rounded-lg">
            <p className="text-error-600">{error}</p>
          </div>
        )}


        {/* Conditional View Rendering */}
        {viewMode === 'kanban' ? (
          <KanbanView
            tickets={tickets}
            onTicketClick={handleTicketClick}
            onDragEnd={handleDragEnd}
            onCreateTicket={() => { }} // Context otomatik yükleyecek
            selectedProject={selectedProject}
            projectStatuses={projectStatuses}
          />
        ) : viewMode === 'list' ? (
          <ListView
            tickets={tickets}
            onTicketClick={handleTicketClick}
          />
        ) : (
          <CalendarView
            tickets={tickets}
            selectedProject={selectedProject}
            onTicketClick={handleTicketClick}
            onDragEnd={handleCalendarDragEnd}
          />
        )}

        {/* Ticket Detail Modal */}
        <TicketDetailModal
          ticket={selectedTicket}
          isOpen={isDetailModalOpen}
          onClose={handleCloseDetailModal}
          onTicketChange={setSelectedTicket}
          onUpdate={async (updatedTicket) => {
            // Ticket'ı context'te güncelle
            if (updatedTicket?.id) {
              try {
                // Context'teki ticket listesini güncelle
                setTickets(prevTickets =>
                  prevTickets.map(ticket =>
                    ticket.id === updatedTicket.id
                      ? { ...ticket, ...updatedTicket }
                      : ticket
                  )
                );
                // Seçili ticket'ı da güncelle
                setSelectedTicket(updatedTicket);
              } catch (error) {
                // Silently handle error
                // Hata durumunda ticket'ları yeniden yükle
                if (loadTickets) {
                  await loadTickets();
                }
              }
            }
          }}
        />

      </div>
    </div>
  );
};

export default BoardPage;